/**
 * useConsent Hook
 *
 * Subscribes to the stored analytics consent decision and exposes whether the
 * consent prompt still needs to be shown.
 */

import { useCallback, useSyncExternalStore } from 'react'

import {
  clearConsent,
  getConsentSnapshot,
  setConsent,
  subscribeToConsent,
  type ConsentState,
} from '@/lib/consent'
import { isAnalyticsConfigured } from '@/lib/analytics'

export interface UseConsentReturn {
  /** Current decision, or null when undecided */
  consent: ConsentState | null

  /** Whether the consent prompt should be shown */
  needsDecision: boolean

  /** Grant consent (enables analytics immediately) */
  accept: () => void

  /** Decline consent (analytics stays off) */
  decline: () => void

  /** Clear the decision so the banner is shown again */
  reset: () => void

  /** Whether this build has analytics configured at all */
  configured: boolean
}

export function useConsent(): UseConsentReturn {
  const consent = useSyncExternalStore(
    subscribeToConsent,
    getConsentSnapshot,
    () => null // server snapshot: never prompt or track during SSR/prerender
  )

  const accept = useCallback(() => setConsent('granted'), [])
  const decline = useCallback(() => setConsent('denied'), [])
  const reset = useCallback(() => clearConsent(), [])
  const configured = isAnalyticsConfigured()

  return {
    consent,
    needsDecision: configured && consent === null,
    accept,
    decline,
    reset,
    configured,
  }
}
